import {
  SITE_URL,
  SITE_NAME,
  SITE_DESCRIPTION,
  SITE_PUBLISHED_DATE,
  absoluteUrl,
  LOGO_PATH,
} from '../config/site';
import type { FAQItem } from '../components/FAQ';
import type { HowToStep } from '../components/HowToUse';

const BUILD_DATE = new Date().toISOString().slice(0, 10);
export const getBuildDate = (): string => BUILD_DATE;

interface OfferSchema {
  '@type': 'Offer';
  price: string;
  priceCurrency: string;
}

export interface WebApplicationSchema {
  '@type': 'WebApplication';
  name: string;
  description: string;
  url: string;
  applicationCategory: string;
  operatingSystem: string;
  offers: OfferSchema;
  featureList: string[];
  datePublished: string;
  dateModified: string;
}

export interface OrganizationSchema {
  '@type': 'Organization';
  name: string;
  url: string;
  logo: string;
  sameAs: string[];
  foundingDate: string;
}

interface FAQQuestion {
  '@type': 'Question';
  name: string;
  acceptedAnswer: { '@type': 'Answer'; text: string };
}

export interface FAQSchema {
  '@type': 'FAQPage';
  mainEntity: FAQQuestion[];
}

interface HowToStepSchema {
  '@type': 'HowToStep';
  name: string;
  text: string;
  position: number;
}

export interface HowToSchema {
  '@type': 'HowTo';
  name: string;
  description: string;
  step: HowToStepSchema[];
  datePublished: string;
  dateModified: string;
}

export interface WebSiteSchema {
  '@type': 'WebSite';
  name: string;
  url: string;
  potentialAction?: {
    '@type': 'SearchAction';
    target: string;
    'query-input': string;
  };
}

export const buildWebApplicationSchema = (): WebApplicationSchema => ({
  '@type': 'WebApplication',
  name: `${SITE_NAME} Converter`,
  description: SITE_DESCRIPTION,
  url: SITE_URL,
  applicationCategory: 'DeveloperApplication',
  operatingSystem: 'Any',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  featureList: [
    'Real-time token counting',
    'JSON syntax validation',
    '30-60% token reduction',
    'Privacy-first architecture',
    'Large file support up to 10MB',
    'One-click copy and download',
    'Dark mode support',
  ],
  datePublished: SITE_PUBLISHED_DATE,
  dateModified: BUILD_DATE,
});

export const buildOrganizationSchema = (): OrganizationSchema => ({
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: absoluteUrl(LOGO_PATH),
  sameAs: [],
  foundingDate: SITE_PUBLISHED_DATE,
});

export const buildFAQSchema = (faqs: FAQItem[]): FAQSchema => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
});

export const buildHowToSchema = (steps: HowToStep[]): HowToSchema => ({
  '@type': 'HowTo',
  name: 'How to Convert JSON to TOON Format',
  description: 'Step-by-step guide to convert JSON to TOON and reduce LLM token costs',
  step: steps.map((s, index) => ({
    '@type': 'HowToStep',
    name: s.title,
    text: s.description,
    position: index + 1,
  })),
  datePublished: SITE_PUBLISHED_DATE,
  dateModified: BUILD_DATE,
});

export const buildWebSiteSchema = (): WebSiteSchema => ({
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

interface GraphInput {
  faqs: FAQItem[];
  howToSteps: HowToStep[];
}

export const buildGraph = ({ faqs, howToSteps }: GraphInput) => ({
  '@context': 'https://schema.org',
  '@graph': [
    buildWebApplicationSchema(),
    buildOrganizationSchema(),
    buildFAQSchema(faqs),
    buildHowToSchema(howToSteps),
    buildWebSiteSchema(),
  ],
});
